import { DatePicker, Flex, Text, TextInput, createForm, useToasts } from '@applyboard/crystal-ui'
import { IAdditionalDocumentDialog, IPassportDocumentRequestDialogForm } from '../types'
import { documentTypeOptions } from '../../../../utils'
import { DocumentTags } from '../../../../utils/enums'
import { FileUploadField } from '../../ApplicationForms/FileUploadField'
import { DocumentDialog } from '../DocumentDialog'
import { useUpdateRequestedDocuments } from '../../../../hooks'
import { useEffect, useState } from 'react'
import { useApplicationFormContext } from '../../ApplicationForms/ApplicationForm'
import { Asterisk } from '../../../Asterisk'
import { ALLOWED_FILE_TYPES } from '../../../../utils/constants'
import { useUploadPassport } from '../../../../hooks/useUploadPassport'
import { transformFileInfoToUploadFileData } from '../../../../utils/transformFileInfoToUploadFileData'
import { DocumentType, RequestedDocuments } from '@applyboard/school-applications-types-lib'
import { useUpdatePassport } from '../../../../hooks/useUpdatePassport'
import { nanoid } from 'nanoid'

type PassportRequestFormFields = {
  passportNumber: string
  passportExpiryDate: string
  passportFiles: Array<{
    id: string
    file: File
  }>
}

type UpdatePassportProps = {
  id?: string
  passportNumber?: string
  passportExpiryDate?: string
}

const { Form, Field, useFieldValues, useSetFieldValues } = createForm<PassportRequestFormFields>()

export function PassportDialog(props: IAdditionalDocumentDialog) {
  const [ passportSectionId ] = useState<string>(nanoid())
  const { resetFiles } = useApplicationFormContext()
  const [open, setOpen] = useState(false)
  const { isUpdatingApplication, updateRequestedDocuments } = useUpdateRequestedDocuments({
    applicationId: props.application.id,
    index: props.index,
  })
  const [updatePassportProps, setUpdatePassportProps] = useState<UpdatePassportProps | null>(null)
  const [newPassportId, setNewPassportId] = useState<string>('')

  const { isUploadingPassport, uploadPassport } = useUploadPassport()
  const toast = useToasts()

  const studentId: string | undefined = props.application.relationships?.student?.data.id

  const { isUpdatingPassport, updatePassport } = useUpdatePassport()
  const [ passportUpdated, setPassportUpdated ] = useState(false)

  const requestedDocuments = props.application.attributes?.requestedDocuments?.[props.id]

  useEffect(() => {
    if (open) {
      setPassportUpdated(false)
      setNewPassportId('')
      setUpdatePassportProps(null)
    }
  }, [open])

  const nationality = props.application.attributes?.personalInformation?.basicPersonalInformation?.nationality
  useEffect(() => {
    if (newPassportId.length > 0 && updatePassportProps && !isUpdatingPassport && !passportUpdated) {
      const updatePassportPayload = {
        id: newPassportId,
        passportNumber: updatePassportProps.passportNumber,
        expiryDate: updatePassportProps.passportExpiryDate,
        nationality: nationality,
      }
      updatePassport(updatePassportPayload, {
        onSuccess: () => {
          setPassportUpdated(true)
          updateRequestedDocuments({
            additionalData: {
              statusAndCitizenship: {
                passports: {
                  [passportSectionId]: {
                    passportId: newPassportId, // use passport ID from the response,
                  },
                },
              },
            },
            dataFiles: { [newPassportId]: null },
            documentType: DocumentTags.PASSPORT,
            onSuccess: () => {
              setOpen(false)
            },
            requestedDocuments: requestedDocuments as RequestedDocuments[string],
            requestedDocumentsId: props.id,
            sectionReference: 'passport',
          })
        },
        onError: error => {
          toast.negative(error)
        }
      })
    }
  }, [newPassportId, updatePassportProps, updatePassport, updateRequestedDocuments, toast, nationality, requestedDocuments, props.id, isUpdatingPassport, passportUpdated, setPassportUpdated])

  if (!requestedDocuments) {
    return null
  }

  const uploadRequestedPassport = (data: PassportRequestFormFields) => {
    try {
      const passportFile = data.passportFiles[0]

      uploadPassport(
        {
          studentId: studentId as string,
          passportDocument: transformFileInfoToUploadFileData(passportFile, {
            sectionReference: 'passport',
            type: DocumentTags.PASSPORT as unknown as DocumentType,
          }),
        },
        {
          onSuccess: response => {
            setNewPassportId(response.data.id)
            setUpdatePassportProps({
              id: response.data.id,
              passportNumber: data.passportNumber,
              passportExpiryDate: data.passportExpiryDate?.substring(0, 10),
            })
          },
          onError: err => {
            toast.negative(err)
          },
        },
      )
    } catch (error) {
      toast.negative(new Error('An error occurred'))
      return
    }
  }

  return (
    <DocumentDialog
      document={props.document}
      heading={`Add ${documentTypeOptions[props.document.documentType].label}`}
      loading={isUpdatingApplication || isUploadingPassport || isUpdatingPassport}
      open={open}
      setOpen={open => {
        resetFiles({})
        setOpen(open)
      }}
      form={`passport-document-request-${props.id}`}
    >
      <Flex direction="column" gap={4}>
        <Text contrast="mid">{props.document.note}</Text>
        <Form
          id={`passport-document-request-${props.id}`}
          defaultValues={{
            passportNumber: '',
            passportExpiryDate: '',
            passportFiles: [],
          }}
          onSubmit={data => uploadRequestedPassport(data)}
        >
          <PassportRequestDialogForm
            application={props.application}
            document={props.document}
          />
        </Form>
      </Flex>
    </DocumentDialog>
  )
}

function PassportRequestDialogForm(props: IPassportDocumentRequestDialogForm) {
  const { getObservableFiles } = useApplicationFormContext()
  const { passportFiles } = useFieldValues(['passportFiles'])
  const setFieldValues = useSetFieldValues()
  const maxDate = new Date()
  const minDate = new Date()
  maxDate.setFullYear(maxDate.getFullYear() + 10)
  minDate.setFullYear(minDate.getFullYear() - 3)

  return (
    <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
      <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
        <Field
          // @ts-ignore
          as={TextInput}
          label="Passport number"
          name="passportNumber"
          required="Passport number is required"
        />
      </Flex.Item>
      <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
        <Field
          as={DatePicker}
          label="Passport expiry date"
          name="passportExpiryDate"
          maxDate={maxDate.toISOString()}
          minDate={minDate.toISOString()}
          required="Passport expiry date is required"
        />
      </Flex.Item>
      <Flex gap={4} direction="column" basis="100%">
        <Field
          as={FileUploadField}
          allowedFileTypes={ALLOWED_FILE_TYPES}
          application={props.application}
          fileLimit={1}
          fileType={DocumentTags.PASSPORT}
          label={
            <Text variant="bodyS" contrast="mid">
              Add the student's passport document below, supported file formats: JPG, JPEG, PDF,
              PNG, max number of files: 1 <Asterisk />
            </Text>
          }
          name={`passportFiles`}
          multiple={false}
          onRemove={(id: string) =>
            setFieldValues({ passportFiles: passportFiles.filter(file => file.id !== id) })
          }
          section="passport"
          showHistory={false}
          validate={value => {
            const observableFiles = getObservableFiles({
              fileType: DocumentTags.PASSPORT,
              sectionReference: 'passport',
            })

            if (Object.keys(observableFiles).length > 1) {
              return `This field has a file limit of ${1}.`
            }

            if (!(Object.keys(observableFiles).length || value.length)) {
              return 'This field is required'
            }
            return true
          }}
        />
      </Flex>
    </Flex>
  )
}
